import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Folder, File, Lock, Globe, Key } from 'lucide-react';

const ArchitectureDiagram = () => {
  const [expandedSections, setExpandedSections] = useState({
    routes: true,
    components: true,
    features: true,
    tech: true
  });

  const [expandedRoutes, setExpandedRoutes] = useState({
    public: true,
    auth: true,
    dashboard: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleRoute = (route) => {
    setExpandedRoutes(prev => ({
      ...prev,
      [route]: !prev[route]
    }));
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f9fafb',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "SF Pro Display", sans-serif'
    },
    header: {
      backgroundColor: '#ffffff',
      borderBottom: '1px solid #e5e7eb',
      padding: '2rem 0'
    },
    headerContent: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '0 1.5rem'
    },
    title: {
      fontSize: '2rem',
      fontWeight: '300',
      color: '#111827',
      margin: 0,
      letterSpacing: '-0.025em'
    },
    subtitle: {
      marginTop: '0.5rem',
      color: '#6b7280',
      fontSize: '1rem'
    },
    main: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '2rem 1.5rem'
    },
    section: {
      marginBottom: '2rem'
    },
    sectionButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      marginBottom: '1rem',
      fontSize: '1.125rem',
      fontWeight: '500',
      color: '#111827',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: 0,
      transition: 'color 0.2s'
    },
    routesContainer: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      border: '1px solid #e5e7eb',
      overflow: 'hidden'
    },
    routeGroup: {
      padding: '1.5rem',
      borderBottom: '1px solid #e5e7eb'
    },
    routeGroupLast: {
      padding: '1.5rem',
      borderBottom: 'none'
    },
    routeGroupButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      marginBottom: '1rem',
      fontWeight: '500',
      color: '#111827',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: 0,
      width: '100%'
    },
    badge: {
      marginLeft: 'auto',
      fontSize: '0.75rem',
      padding: '0.125rem 0.5rem',
      borderRadius: '0.25rem',
      fontWeight: '500',
      textTransform: 'uppercase',
      letterSpacing: '0.025em'
    },
    publicBadge: {
      backgroundColor: '#dcfce7',
      color: '#16a34a'
    },
    authBadge: {
      backgroundColor: '#fee2e2',
      color: '#dc2626'
    },
    protectedBadge: {
      backgroundColor: '#dbeafe',
      color: '#2563eb'
    },
    routesList: {
      marginLeft: '2.5rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem'
    },
    routeItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.375rem 0',
      color: '#6b7280',
      transition: 'color 0.2s',
      cursor: 'default'
    },
    routeItemHover: {
      color: '#111827'
    },
    routeCode: {
      fontFamily: 'Monaco, Consolas, "Courier New", monospace',
      fontSize: '0.875rem',
      color: '#374151'
    },
    routeDescription: {
      fontSize: '0.875rem'
    },
    highlightedRoute: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.375rem 0',
      color: '#2563eb',
      fontWeight: '500'
    },
    coreFeatureBadge: {
      marginLeft: '0.5rem',
      fontSize: '0.6875rem',
      padding: '0.125rem 0.5rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      borderRadius: '0.25rem'
    },
    componentsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '1rem'
    },
    componentCard: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      border: '1px solid #e5e7eb',
      padding: '1rem'
    },
    highlightedCard: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      border: '2px solid #93c5fd',
      padding: '1rem',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
    },
    componentTitle: {
      fontWeight: '500',
      color: '#111827',
      marginBottom: '0.75rem',
      fontSize: '0.9375rem'
    },
    componentList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem'
    },
    componentItem: {
      fontSize: '0.875rem',
      color: '#6b7280',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    bullet: {
      width: '0.25rem',
      height: '0.25rem',
      backgroundColor: '#9ca3af',
      borderRadius: '50%'
    },
    featuresContainer: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      border: '1px solid #e5e7eb',
      padding: '1.5rem'
    },
    featuresGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1.5rem'
    },
    featureBlock: {
      display: 'flex',
      flexDirection: 'column'
    },
    featureTitle: {
      fontWeight: '500',
      color: '#111827',
      marginBottom: '0.5rem',
      fontSize: '0.9375rem'
    },
    featureList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem'
    },
    featureItem: {
      fontSize: '0.875rem',
      color: '#6b7280'
    },
    techGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '2rem'
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <h1 style={styles.title}>CityGetAway Architecture</h1>
          <p style={styles.subtitle}>Next.js 14 Application Structure</p>
        </div>
      </header>

      <div style={styles.main}>
        {/* Routes Section */}
        <section style={styles.section}>
          <button
            onClick={() => toggleSection('routes')}
            style={styles.sectionButton}
            onMouseEnter={(e) => e.target.style.color = '#6b7280'}
            onMouseLeave={(e) => e.target.style.color = '#111827'}
          >
            {expandedSections.routes ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            Application Routes
          </button>
          
          {expandedSections.routes && (
            <div style={styles.routesContainer}>
              {/* Public Routes */}
              <div style={styles.routeGroup}>
                <button
                  onClick={() => toggleRoute('public')}
                  style={styles.routeGroupButton}
                >
                  {expandedRoutes.public ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <Globe size={18} color="#16a34a" />
                  <span>Public Routes</span>
                  <span style={{...styles.badge, ...styles.publicBadge}}>No Auth Required</span>
                </button>
                
                {expandedRoutes.public && (
                  <div style={styles.routesList}>
                    {[
                      { path: '/', name: 'Landing Page', files: ['page.tsx', 'hero.tsx', 'features.tsx'] },
                      { path: '/features', name: 'Features', files: ['page.tsx', 'feature-grid.tsx'] },
                      { path: '/pricing', name: 'Pricing', files: ['page.tsx', 'pricing-table.tsx', 'plan-card.tsx'] },
                      { path: '/blog', name: 'Blog', files: ['page.tsx', 'layout.tsx', '[slug]/page.tsx'] },
                      { path: '/contact', name: 'Contact', files: ['page.tsx', 'contact-form.tsx'] },
                      { path: '/destinations', name: 'Destinations', files: ['page.tsx', '[city]/page.tsx', 'city-card.tsx'] },
                      { path: '/get-started', name: 'Get Started', files: ['page.tsx', 'destination-selector.tsx'] }
                    ].map(route => (
                      <div key={route.path}>
                        <div 
                          style={styles.routeItem}
                          onMouseEnter={(e) => e.currentTarget.style.color = '#111827'}
                          onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}
                        >
                          <File size={14} />
                          <code style={styles.routeCode}>{route.path}</code>
                          <span style={styles.routeDescription}>- {route.name}</span>
                        </div>
                        <div style={{marginLeft: '3rem', fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem'}}>
                          {route.files.join(', ')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Auth Routes */}
              <div style={styles.routeGroup}>
                <button
                  onClick={() => toggleRoute('auth')}
                  style={styles.routeGroupButton}
                >
                  {expandedRoutes.auth ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <Key size={18} color="#dc2626" />
                  <span>Authentication Routes</span>
                  <span style={{...styles.badge, ...styles.authBadge}}>Auth Flow</span>
                </button>
                
                {expandedRoutes.auth && (
                  <div style={styles.routesList}>
                    {[
                      { path: '/sign-in', name: 'Sign In', files: ['page.tsx', 'signin-form.tsx', 'oauth-buttons.tsx'] },
                      { path: '/sign-up', name: 'Sign Up', files: ['page.tsx', 'signup-form.tsx', 'terms-checkbox.tsx'] },
                      { path: '/forgot-password', name: 'Password Reset', files: ['page.tsx', 'reset-form.tsx', 'email-sent.tsx'] },
                      { path: '/verify-email', name: 'Email Verification', files: ['page.tsx', 'verification-code.tsx'] }
                    ].map(route => (
                      <div key={route.path}>
                        <div 
                          style={styles.routeItem}
                          onMouseEnter={(e) => e.currentTarget.style.color = '#111827'}
                          onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}
                        >
                          <File size={14} />
                          <code style={styles.routeCode}>{route.path}</code>
                          <span style={styles.routeDescription}>- {route.name}</span>
                        </div>
                        <div style={{marginLeft: '3rem', fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem'}}>
                          {route.files.join(', ')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Dashboard Routes */}
              <div style={styles.routeGroupLast}>
                <button
                  onClick={() => toggleRoute('dashboard')}
                  style={styles.routeGroupButton}
                >
                  {expandedRoutes.dashboard ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <Lock size={18} color="#2563eb" />
                  <span>Dashboard Routes</span>
                  <span style={{...styles.badge, ...styles.protectedBadge}}>Protected</span>
                </button>
                
                {expandedRoutes.dashboard && (
                  <div style={styles.routesList}>
                    {[
                      { path: '/', name: 'Dashboard Home', files: ['page.tsx', 'layout.tsx', 'loading.tsx', 'error.tsx'] },
                      { path: '/trip-planner', name: 'AI Trip Assistant', highlight: true, files: ['page.tsx', 'trip-context.tsx', 'chat-interface.tsx', 'itinerary-sidebar.tsx'] },
                      { path: '/city-guide', name: 'City Guide', files: ['page.tsx', 'map-view.tsx', 'place-details.tsx'] },
                      { path: '/community', name: 'Community', files: ['page.tsx', 'post-list.tsx', '[postId]/page.tsx'] },
                      { path: '/messages', name: 'Messages', files: ['page.tsx', 'conversation-list.tsx', 'message-thread.tsx'] },
                      { path: '/profile', name: 'Profile', files: ['page.tsx', 'profile-form.tsx', 'avatar-upload.tsx'] },
                      { path: '/settings', name: 'Settings', files: ['page.tsx', 'preferences.tsx', 'security.tsx'] }
                    ].map(route => (
                      <div key={route.path}>
                        <div 
                          style={route.highlight ? styles.highlightedRoute : styles.routeItem}
                          onMouseEnter={(e) => !route.highlight && (e.currentTarget.style.color = '#111827')}
                          onMouseLeave={(e) => !route.highlight && (e.currentTarget.style.color = '#6b7280')}
                        >
                          <File size={14} />
                          <code style={styles.routeCode}>{route.path}</code>
                          <span style={styles.routeDescription}>- {route.name}</span>
                          {route.highlight && (
                            <span style={styles.coreFeatureBadge}>Core Feature</span>
                          )}
                        </div>
                        <div style={{marginLeft: '3rem', fontSize: '0.75rem', color: route.highlight ? '#3b82f6' : '#9ca3af', marginTop: '0.25rem'}}>
                          {route.files.join(', ')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </section>

        {/* Components Section */}
        <section style={styles.section}>
          <button
            onClick={() => toggleSection('components')}
            style={styles.sectionButton}
            onMouseEnter={(e) => e.target.style.color = '#6b7280'}
            onMouseLeave={(e) => e.target.style.color = '#111827'}
          >
            {expandedSections.components ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            Component Structure
          </button>
          
          {expandedSections.components && (
            <div style={styles.componentsGrid}>
              {[
                {
                  name: 'Landing Components',
                  path: 'components/landing/',
                  items: [
                    { name: 'Hero Section', desc: 'HeroSection.tsx, HeroBackground.tsx, AnimatedText.tsx' },
                    { name: 'Features Grid', desc: 'FeatureGrid.tsx, FeatureCard.tsx, FeatureIcon.tsx' },
                    { name: 'Stats Bar', desc: 'StatsBar.tsx, StatItem.tsx, CountAnimation.tsx' },
                    { name: 'City Showcase', desc: 'CityShowcase.tsx, CityCard.tsx, RatingStars.tsx' },
                    { name: 'CTA Buttons', desc: 'CTASection.tsx, GetStartedButton.tsx, WatchDemoButton.tsx' }
                  ]
                },
                {
                  name: 'Dashboard Components',
                  path: 'components/dashboard/',
                  items: [
                    { name: 'Weather Widget', desc: 'WeatherCard.tsx, Temperature.tsx, WeatherDetails.tsx, Forecast.tsx' },
                    { name: 'Quick Tools', desc: 'QuickTools.tsx, ToolCard.tsx, Navigation.tsx, Currency.tsx' },
                    { name: 'Service Cards', desc: 'ServiceCards.tsx, TripPlannerCard.tsx, CityGuideCard.tsx' },
                    { name: 'Destination Explorer', desc: 'DestinationGrid.tsx, DestinationCard.tsx, PriceTag.tsx' },
                    { name: 'Filter Bar', desc: 'FilterBar.tsx, FilterButton.tsx, SortDropdown.tsx' }
                  ]
                },
                {
                  name: 'Trip Planner Components',
                  path: 'components/trip-planner/',
                  items: [
                    { name: 'TripContext (Left)', desc: 'TripContext.tsx, DestinationInput.tsx, DatePicker.tsx, BudgetSlider.tsx, PreferencesPanel.tsx' },
                    { name: 'ChatInterface (Center)', desc: 'ChatInterface.tsx, MessageList.tsx, MessageBubble.tsx, TypingIndicator.tsx, SuggestedActions.tsx' },
                    { name: 'ItinerarySidebar (Right)', desc: 'ItinerarySidebar.tsx, DayPlan.tsx, PlaceCard.tsx, MapPreview.tsx, SmartPicks.tsx' },
                    { name: 'Message Components', desc: 'AIMessage.tsx, UserMessage.tsx, SystemMessage.tsx, MessageActions.tsx' },
                    { name: 'Input Components', desc: 'ChatInput.tsx, AttachmentButton.tsx, SendButton.tsx, VoiceInput.tsx' }
                  ],
                  highlight: true
                },
                {
                  name: 'Shared Components',
                  path: 'components/shared/',
                  items: [
                    { name: 'Navigation', desc: 'Navbar.tsx, NavMenu.tsx, Logo.tsx, UserDropdown.tsx, NotificationBell.tsx' },
                    { name: 'Layout', desc: 'PageLayout.tsx, Container.tsx, Grid.tsx, Flex.tsx' },
                    { name: 'Form Elements', desc: 'Input.tsx, Select.tsx, Checkbox.tsx, Radio.tsx, Switch.tsx, Slider.tsx' },
                    { name: 'Feedback', desc: 'Modal.tsx, Toast.tsx, Alert.tsx, Tooltip.tsx, Popover.tsx' },
                    { name: 'Display', desc: 'Card.tsx, Badge.tsx, Avatar.tsx, Skeleton.tsx, Spinner.tsx' }
                  ]
                },
                {
                  name: 'Onboarding Components',
                  path: 'components/onboarding/',
                  items: [
                    { name: 'Destination Selector', desc: 'DestinationSelector.tsx, SearchBar.tsx, SearchResults.tsx' },
                    { name: 'City Cards', desc: 'CityGrid.tsx, CityCard.tsx, CityImage.tsx, CityInfo.tsx' },
                    { name: 'Filter System', desc: 'FilterTabs.tsx, CategoryFilter.tsx, TagFilter.tsx' },
                    { name: 'City Quiz', desc: 'CityQuiz.tsx, QuizQuestion.tsx, QuizResults.tsx, ProgressBar.tsx' },
                    { name: 'Welcome Flow', desc: 'WelcomeScreen.tsx, StepIndicator.tsx, NextButton.tsx' }
                  ]
                },
                {
                  name: 'Auth Components',
                  path: 'components/auth/',
                  items: [
                    { name: 'Sign In', desc: 'SignInForm.tsx, EmailInput.tsx, PasswordInput.tsx, RememberMe.tsx' },
                    { name: 'Sign Up', desc: 'SignUpForm.tsx, NameInput.tsx, PasswordStrength.tsx, TermsCheckbox.tsx' },
                    { name: 'OAuth', desc: 'OAuthButtons.tsx, GoogleButton.tsx, FacebookButton.tsx, AppleButton.tsx' },
                    { name: 'Password', desc: 'ForgotPassword.tsx, ResetPassword.tsx, PasswordRequirements.tsx' },
                    { name: 'Verification', desc: 'EmailVerification.tsx, CodeInput.tsx, ResendButton.tsx' }
                  ]
                }
              ].map(component => (
                <div 
                  key={component.name} 
                  style={component.highlight ? styles.highlightedCard : styles.componentCard}
                >
                  <h3 style={styles.componentTitle}>{component.name}</h3>
                  <div style={{fontSize: '0.7rem', color: '#9ca3af', marginBottom: '0.75rem', fontFamily: 'Monaco, Consolas, monospace'}}>
                    {component.path}
                  </div>
                  <ul style={styles.componentList}>
                    {component.items.map(item => (
                      <li key={item.name} style={{marginBottom: '0.75rem'}}>
                        <div style={styles.componentItem}>
                          <span style={styles.bullet}></span>
                          <span style={{fontWeight: '500'}}>{item.name}</span>
                        </div>
                        <div style={{marginLeft: '0.75rem', fontSize: '0.7rem', color: '#9ca3af', marginTop: '0.25rem'}}>
                          {item.desc}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Features Section */}
        <section style={styles.section}>
          <button
            onClick={() => toggleSection('features')}
            style={styles.sectionButton}
            onMouseEnter={(e) => e.target.style.color = '#6b7280'}
            onMouseLeave={(e) => e.target.style.color = '#111827'}
          >
            {expandedSections.features ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            Feature Modules
          </button>
          
          {expandedSections.features && (
            <div style={styles.featuresContainer}>
              <div style={styles.featuresGrid}>
                {[
                  {
                    name: 'Authentication',
                    path: 'features/auth/',
                    items: [
                      { file: 'hooks/useAuth.ts', desc: 'login, logout, currentUser, isAuthenticated' },
                      { file: 'services/authService.ts', desc: 'signIn, signUp, refreshToken, verifyEmail' },
                      { file: 'utils/jwt.ts', desc: 'decodeToken, isTokenExpired, getRefreshToken' },
                      { file: 'stores/authStore.ts', desc: 'user state, token management, session persistence' }
                    ]
                  },
                  {
                    name: 'Destinations',
                    path: 'features/destinations/',
                    items: [
                      { file: 'hooks/useDestinations.ts', desc: 'fetchCities, searchDestinations, filterByCategory' },
                      { file: 'services/destinationService.ts', desc: 'getCityDetails, getAttractions, getRestaurants' },
                      { file: 'utils/cityData.ts', desc: 'cityDatabase, popularDestinations, categories' },
                      { file: 'types/destination.ts', desc: 'City, Attraction, Restaurant, Hotel interfaces' }
                    ]
                  },
                  {
                    name: 'Trip Planning',
                    path: 'features/trip-planning/',
                    items: [
                      { file: 'hooks/useTripPlanner.ts', desc: 'createTrip, updateItinerary, saveTrip' },
                      { file: 'hooks/useAIAssistant.ts', desc: 'sendMessage, generateItinerary, getSuggestions' },
                      { file: 'services/openai.ts', desc: 'chatCompletion, streamResponse, promptTemplates' },
                      { file: 'stores/tripStore.ts', desc: 'currentTrip, messages, itinerary, preferences' }
                    ]
                  },
                  {
                    name: 'Weather',
                    path: 'features/weather/',
                    items: [
                      { file: 'hooks/useWeather.ts', desc: 'getCurrentWeather, getForecast, getAlerts' },
                      { file: 'services/weatherAPI.ts', desc: 'fetchWeatherData, parseResponse, cacheResults' },
                      { file: 'utils/weatherIcons.ts', desc: 'icon mappings, conditions, temperature conversion' },
                      { file: 'types/weather.ts', desc: 'WeatherData, Forecast, WeatherAlert interfaces' }
                    ]
                  }
                ].map(feature => (
                  <div key={feature.name} style={styles.featureBlock}>
                    <h4 style={styles.featureTitle}>{feature.name}</h4>
                    <div style={{fontSize: '0.7rem', color: '#9ca3af', marginBottom: '0.5rem', fontFamily: 'Monaco, Consolas, monospace'}}>
                      {feature.path}
                    </div>
                    <ul style={styles.featureList}>
                      {feature.items.map(item => (
                        <li key={item.file} style={{marginBottom: '0.5rem'}}>
                          <div style={{fontSize: '0.8rem', color: '#374151', fontFamily: 'Monaco, Consolas, monospace'}}>
                            {item.file}
                          </div>
                          <div style={{fontSize: '0.7rem', color: '#9ca3af', marginTop: '0.125rem'}}>
                            {item.desc}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Tech Stack */}
        <section>
          <button
            onClick={() => toggleSection('tech')}
            style={styles.sectionButton}
            onMouseEnter={(e) => e.target.style.color = '#6b7280'}
            onMouseLeave={(e) => e.target.style.color = '#111827'}
          >
            {expandedSections.tech ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            Technology Stack
          </button>
          
          {expandedSections.tech && (
            <div style={styles.featuresContainer}>
              <div style={styles.techGrid}>
                <div style={styles.featureBlock}>
                  <h4 style={styles.featureTitle}>Frontend</h4>
                  <ul style={styles.featureList}>
                    <li style={styles.featureItem}>Next.js 14</li>
                    <li style={styles.featureItem}>TypeScript</li>
                    <li style={styles.featureItem}>Tailwind CSS</li>
                    <li style={styles.featureItem}>Framer Motion</li>
                  </ul>
                </div>
                <div style={styles.featureBlock}>
                  <h4 style={styles.featureTitle}>State</h4>
                  <ul style={styles.featureList}>
                    <li style={styles.featureItem}>Zustand</li>
                    <li style={styles.featureItem}>React Context</li>
                    <li style={styles.featureItem}>TanStack Query</li>
                    <li style={styles.featureItem}>Local Storage</li>
                  </ul>
                </div>
                <div style={styles.featureBlock}>
                  <h4 style={styles.featureTitle}>Backend</h4>
                  <ul style={styles.featureList}>
                    <li style={styles.featureItem}>API Routes</li>
                    <li style={styles.featureItem}>OpenAI API</li>
                    <li style={styles.featureItem}>Prisma ORM</li>
                    <li style={styles.featureItem}>PostgreSQL</li>
                  </ul>
                </div>
                <div style={styles.featureBlock}>
                  <h4 style={styles.featureTitle}>Tools</h4>
                  <ul style={styles.featureList}>
                    <li style={styles.featureItem}>ESLint</li>
                    <li style={styles.featureItem}>Prettier</li>
                    <li style={styles.featureItem}>Jest/Vitest</li>
                    <li style={styles.featureItem}>Storybook</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ArchitectureDiagram;